var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useDebounce from './useDebounce';
var usePrevious = function (value) {
    var ref = useRef();
    var prev = ref.current;
    ref.current = value;
    return prev;
};
export var useLegacyPopoverState = function (_a) {
    var _b = _a.closeOnClickInsidePopover, closeOnClickInsidePopover = _b === void 0 ? false : _b, _c = _a.closeOnClickOutsidePopoverAndReference, closeOnClickOutsidePopoverAndReference = _c === void 0 ? true : _c, _d = _a.delayOnMouseEnter, delayOnMouseEnter = _d === void 0 ? 50 : _d, _e = _a.delayOnMouseLeave, delayOnMouseLeave = _e === void 0 ? 0 : _e, _f = _a.enabled, enabled = _f === void 0 ? true : _f, onCloseStart = _a.onCloseStart, onOpenStart = _a.onOpenStart, showOnClick = _a.showOnClick, showOnHover = _a.showOnHover;
    var mounted = useRef(false);
    useEffect(function () {
        mounted.current = true;
        return function () {
            mounted.current = false;
        };
    }, []);
    var _g = __read(useState({ show: false }), 2), show = _g[0].show, setState = _g[1];
    var showPrev = usePrevious(show);
    useEffect(function () {
        if (onCloseStart && !show && showPrev === true) {
            onCloseStart();
        }
        else if (onOpenStart && show && showPrev === false) {
            onOpenStart();
        }
    }, [onCloseStart, onOpenStart, show, showPrev]);
    var debouncedSetOpenWithEnterDelay = useDebounce(function (value) {
        if (mounted.current) {
            setState(value);
        }
    }, delayOnMouseEnter, {
        leading: false,
        trailing: true,
    }, [delayOnMouseEnter]);
    var debouncedSetOpenWithLeaveDelay = useDebounce(function (value) {
        if (mounted.current) {
            setState(value);
        }
    }, delayOnMouseLeave, {
        leading: false,
        trailing: true,
    }, [delayOnMouseLeave]);
    var handleMouseEnter = useMemo(function () {
        if (!enabled || !showOnHover) {
            return undefined;
        }
        return function () {
            debouncedSetOpenWithLeaveDelay && debouncedSetOpenWithLeaveDelay.cancel();
            debouncedSetOpenWithEnterDelay(function (currentState) {
                return !currentState.show ? { show: true, source: 'hover' } : currentState;
            });
        };
    }, [debouncedSetOpenWithEnterDelay, debouncedSetOpenWithLeaveDelay, enabled, showOnHover]);
    var handleMouseLeave = useMemo(function () {
        if (!enabled || !showOnHover) {
            return undefined;
        }
        return function () {
            debouncedSetOpenWithEnterDelay && debouncedSetOpenWithEnterDelay.cancel();
            debouncedSetOpenWithLeaveDelay(function (currentState) {
                return currentState.show && currentState.source === 'hover'
                    ? { show: false }
                    : currentState;
            });
        };
    }, [debouncedSetOpenWithEnterDelay, debouncedSetOpenWithLeaveDelay, enabled, showOnHover]);
    var handleClickRef = useMemo(function () {
        if (!enabled || !showOnClick) {
            return undefined;
        }
        var handler = function (event) {
            /* Fixes an issue where the click was propagating up, effectively closing the
             * Popover immediately after it had been opened. */
            event.stopPropagation();
            setState(function (currentState) {
                if (currentState.show && currentState.source !== 'hover') {
                    return { show: false };
                }
                return { show: true, source: 'click' };
            });
        };
        return handler;
    }, [enabled, showOnClick]);
    var handleClickInside = useMemo(function () {
        if (!enabled || !closeOnClickInsidePopover) {
            return undefined;
        }
        return function () {
            setState({ show: false });
        };
    }, [closeOnClickInsidePopover, enabled]);
    var handleClickOutsideContentAndPositionRef = useMemo(function () {
        if (!enabled || !showOnClick || !closeOnClickOutsidePopoverAndReference) {
            return undefined;
        }
        return function () {
            setState({ show: false });
        };
    }, [closeOnClickOutsidePopoverAndReference, enabled, showOnClick]);
    var open = useCallback(function () {
        if (enabled) {
            setState({ show: true, source: 'custom' });
        }
    }, [enabled]);
    var close = useCallback(function () {
        if (enabled) {
            setState({ show: false });
        }
    }, [enabled]);
    return {
        close: close,
        open: open,
        popoverHandlers: {
            show: show,
            onClickInside: handleClickInside,
            onClickOutsideContentAndPositionRef: handleClickOutsideContentAndPositionRef,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
        },
        referenceHandlers: {
            onClick: handleClickRef,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
        },
    };
};
//# sourceMappingURL=useLegacyPopoverState.js.map